import type { Graph } from "../graph";
import { nodePenalties } from "../runnability";
import { Flag, Hw, Surface } from "../tags";

/** User preferences, each in [-1, 1]. */
export interface Prefs {
  /** -1 = prefer roads/paved, +1 = prefer trails/unpaved. */
  trail: number;
  /** -1 = avoid climbing, +1 = seek hills. */
  hills: number;
  /**
   * Prefer marked running trails (ADR 0006). Default (undefined) is on. Off: no trail cost
   * discount, no trail score term, no trail options.
   */
  trails?: boolean;
}

export const DEFAULT_PREFS: Prefs = { trail: 0, hills: 0, trails: true };

/** Whether running trails steer this request (`Prefs.trails`, default on). */
export const trailsOn = (p: Prefs): boolean => p.trails !== false;

/**
 * Weights for popularity and green share in the edge cost, tuned on live Djurgården and Sea Point
 * traces plus the fixtures (ADR 0005). Without traces pop = 0, so only W_GREEN matters.
 */
export const W_POP = 0.5;
export const W_GREEN = 0.4;
/**
 * Learned runnability (ADR 0007, `test/runnability.fit.live.test.ts`, 8 Stockholm areas, held-out
 * AUC 0.68–0.78). Each weight is W_GREEN × (its logit coefficient / green's), so the fit sets the
 * ratios and ADR 0005's live-tuned W_GREEN sets the scale. Water: 0.254 / 0.385 → 0.25 on top of
 * green (water areas are also green), lowered to 0.1 by the live A/B: 0.25 drew routes to the
 * shore off the paths runners use (ADR 0007). Unpaved: 0.865 / 0.385 would be 0.9; capped at 0.15 because
 * unpaved ways sit in forests already counted as green.
 */
export const W_WATER = 0.1;
export const W_UNPAVED = 0.15;

/**
 * Running trails (ADR 0006): edge cost × (1 − W_TRAIL · trailS) and score + W_TRAIL_SCORE ·
 * (1 − trailScore). The combined pop/green/trail multiplier is floored at PREF_FLOOR, so trails and
 * run-only popularity (which correlate) cannot stack into long detours. Without trails the floor
 * equals the pop/green minimum (0.5 · 0.6), so it changes nothing there.
 */
export const W_TRAIL = 0.35;
/** Tuned live in task 8.8 (ADR 0006 follow-up): 0.4 → 0.8, W_TRAIL kept at 0.35. */
export const W_TRAIL_SCORE = 0.8;
export const PREF_FLOOR = 0.3;

/** max(PREF_FLOOR, (1 − wPop·pop)(1 − wGreen·green)(1 − wTrail·trailS)(1 − W_WATER·water)). */
export function prefFactor(
  pop: number,
  green: number,
  trailS: number,
  wPop: number,
  wGreen: number,
  wTrail: number,
  water = 0,
  wWater = W_WATER,
): number {
  return Math.max(
    PREF_FLOOR,
    (1 - wPop * pop) * (1 - wGreen * green) * (1 - wTrail * trailS) * (1 - wWater * water),
  );
}

// Base multiplier per highway class (1 = neutral). Busy roads cost more; dedicated paths less.
const HW_BASE: Readonly<Record<number, number>> = {
  [Hw.Other]: 1.4,
  [Hw.Footway]: 1.0,
  [Hw.Path]: 1.0,
  [Hw.Track]: 1.05,
  [Hw.Pedestrian]: 1.0,
  [Hw.Steps]: 2.5,
  // Learned (ADR 0007): shared foot-and-cycle paths are run at least as much as footways.
  [Hw.Cycleway]: 1.0,
  [Hw.LivingStreet]: 1.1,
  [Hw.Residential]: 1.2,
  [Hw.Service]: 1.3,
  [Hw.Unclassified]: 1.3,
  [Hw.Tertiary]: 1.5,
  [Hw.Secondary]: 1.9,
  [Hw.Primary]: 2.5,
  [Hw.Bridleway]: 1.2,
};

const TRAILY = new Set<number>([Hw.Path, Hw.Track, Hw.Bridleway]);
const ROADY = new Set<number>([
  Hw.Residential,
  Hw.LivingStreet,
  Hw.Unclassified,
  Hw.Tertiary,
  Hw.Secondary,
  Hw.Primary,
  Hw.Service,
]);

export function tagPenalty(
  highway: number,
  surface: number,
  flags: number,
  trail: number,
  learned = true,
): number {
  let m = HW_BASE[highway] ?? 1.4;
  // Before ADR 0007 cycleways cost 1.1 (kept for A/B checks).
  if (!learned && highway === Hw.Cycleway) m = 1.1;
  // A sidewalk makes a road far more pleasant.
  if (flags & Flag.Sidewalk && ROADY.has(highway)) m = 1 + (m - 1) * 0.5;
  const unpaved =
    surface === Surface.Unpaved || (surface === Surface.Unknown && TRAILY.has(highway));
  const paved = surface === Surface.Paved;
  // trail > 0 rewards unpaved/trail ways and taxes roads; trail < 0 the other way round.
  // Learned (ADR 0007): runners pick gravel and dirt even with a neutral trail preference.
  if (learned && unpaved && trail === 0) m *= 1 - W_UNPAVED;
  if (trail > 0) {
    if (unpaved || TRAILY.has(highway)) m *= 1 - 0.3 * trail;
    if (ROADY.has(highway)) m *= 1 + 0.5 * trail;
  } else if (trail < 0) {
    if (unpaved) m *= 1 - 0.8 * trail; // trail is negative → penalty
    if (paved) m *= 1 + 0.2 * trail;
  }
  return m;
}

/** Multiplier for going uphill by `climb` metres over `len` metres. Always > 0.5. */
export function hillFactor(climb: number, len: number, hills: number): number {
  if (len <= 0) return 1;
  const grade = Math.max(0, climb) / len;
  // ~ +6% grade doubles the cost when avoiding hills; seeking hills halves it at most.
  if (hills < 0) return 1 + -hills * grade * 16;
  if (hills > 0) return Math.max(0.5, 1 - hills * grade * 6);
  return 1 + grade * 2;
}

export interface Costs {
  /** Cost to traverse e from u to v, and v to u. */
  uv: Float32Array;
  vu: Float32Array;
  /** Lower bound on cost per metre over all edges (for an admissible A* heuristic). */
  minPerMetre: number;
}

/** ADR 0007 switches, for A/B checks against the pre-0007 model. Default: everything on. */
export interface CostOptions {
  /** Water weight (default W_WATER). */
  water?: number;
  /** Road-crossing and traffic-light node costs (default on). */
  streets?: boolean;
  /** Scale on node costs (default 1; for tuning). */
  streetScale?: number;
  /** Learned class and surface adjustments (default on). */
  learned?: boolean;
}

export function edgeCosts(
  g: Graph,
  prefs: Prefs,
  wPop = W_POP,
  wGreen = W_GREEN,
  wTrail = W_TRAIL,
  opts: CostOptions = {},
): Costs {
  const wWater = opts.water ?? W_WATER;
  const learned = opts.learned !== false;
  const streetScale = opts.streetScale ?? 1;
  const wT = trailsOn(prefs) ? wTrail : 0;
  const uv = new Float32Array(g.edgeCount);
  const vu = new Float32Array(g.edgeCount);
  let minPerMetre = Infinity;
  // Road crossings and traffic lights (ADR 0007): half of each node's cost on every edge at it,
  // so a route passing through a node pays it once. Additive, so minPerMetre stays a bound.
  const np = opts.streets === false ? new Float32Array(g.nodeCount) : nodePenalties(g);
  for (let e = 0; e < g.edgeCount; e++) {
    const len = g.len[e] as number;
    const base =
      len *
      tagPenalty(
        g.highway[e] as number,
        g.surface[e] as number,
        g.flags[e] as number,
        prefs.trail,
        learned,
      ) *
      prefFactor(
        g.pop[e] as number,
        g.green[e] as number,
        g.trailS[e] as number,
        wPop,
        wGreen,
        wT,
        g.water[e] as number,
        wWater,
      );
    const a = base * hillFactor(g.climbUV[e] as number, len, prefs.hills);
    const b = base * hillFactor(g.climbVU[e] as number, len, prefs.hills);
    if (len > 0) minPerMetre = Math.min(minPerMetre, a / len, b / len);
    const node =
      0.5 * streetScale * ((np[g.u[e] as number] as number) + (np[g.v[e] as number] as number));
    uv[e] = a + node;
    vu[e] = b + node;
  }
  // Slack for float32 rounding so the heuristic stays a true lower bound.
  minPerMetre = Number.isFinite(minPerMetre) ? minPerMetre * 0.999 : 0.1;
  return { uv, vu, minPerMetre };
}
